import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './guards/ProtectedRoute';
import './i18n/config';
import Login from './views/Login/Login';
import LoginCallback from './views/Login/LoginCallback';
import ChangePassword from './views/ChangePassword/ChangePassword';
import Dashboard from './views/Dashboard/Dashboard';
import SuperAdminDashboard from './views/SuperAdminDashboard/SuperAdminDashboard';
import TenantManagement from './views/TenantManagement/TenantManagement';
import UserManagement from './views/UserManagement/UserManagement';
import RoleManagement from './views/RoleManagement/RoleManagement';
import UserGroupManagement from './views/UserGroupManagement/UserGroupManagement';
import Unauthorized from './views/Unauthorized/Unauthorized';
import {
  MarketInterestCheck,
  ThankYouMessage,
  SelfRegistrationStep1,
  SelfRegistrationConsent,
  SelfRegistrationStep2,
  SelfRegistrationStep3,
  SelfRegistrationSuccess,
  InvestorSetPassword
} from './views/Investor/InvestorRegistration';
import { IntroducedInvestorWizard } from './views/Investor/IntroducedInvestorWizard/IntroducedInvestorWizard';
import { 
  IntroduceInvestor1Redirect,
  IntroducedInvestorStart,
  IntroducedInvestorConsent,
  IntroducedInvestorStep1,
  IntroducedInvestorStep2,
  IntroducedInvestorStep4,
  IntroducedInvestorSuccess
} from './views/Investor/IntroducedRegistration';
import { PmsInvestorWizard } from './views/Investor/PmsInvestorWizard/PmsInvestorWizard';
import { BatchRegistrationForm } from './views/BatchRegistration/BatchRegistrationForm';
import { AdminInvestorList } from './views/Admin/AdminInvestorList/AdminInvestorList';
import { AdminInvestorDetails } from './views/Admin/AdminInvestorDetails/AdminInvestorDetails';
import { BrokerDashboard } from './views/Broker/BrokerDashboard/BrokerDashboard';
import { MyAgents } from './views/Broker/MyAgents/MyAgents';
import { InvestorDashboard } from './views/Investor/InvestorDashboard/InvestorDashboard';
import { InvestorProgress } from './views/Investor/InvestorProgress/InvestorProgress';
import { InformationContainer } from './views/Investor/InformationForms/InformationContainer';
import { JourneyList } from './views/Investor/JourneyList/JourneyList';
import { MyProfile } from './views/Investor/MyProfile/MyProfile';
import { MyConsents } from './views/Investor/MyConsents/MyConsents';
import { DsrCenter } from './views/Investor/DsrCenter/DsrCenter';
import { DsrRequests } from './views/Investor/DsrCenter/DsrRequests';
import { DsrCaseDetail } from './views/Investor/DsrCenter/DsrCaseDetail';
import { DocumentUpload } from './views/Investor/DocumentUpload/DocumentUpload';
import { SmartUploadTab } from './views/Investor/InvestorDashboard/components/SmartUploadTab';
import { OnboardingDocuments } from './views/Investor/OnboardingDocuments/OnboardingDocuments';
import { PublicDocumentSubmission } from './views/Investor/PublicDocumentSubmission/PublicDocumentSubmission';
import { ReEditAction } from './views/ReEditAction/ReEditAction';
import { InvestorProfilePdf } from './views/Investor/InvestorProfilePdf/InvestorProfilePdf';
import { UserProfile } from './views/User/UserProfile/UserProfile';
import { InvestorTypeCategoryManagement } from './views/Admin/InvestorTypeCategory/InvestorTypeCategory';
import { MarketTypeManagement } from './views/Admin/MarketType/MarketType';
import { NationalityManagement } from './views/Admin/Nationality/Nationality';
import { ServiceAgreement } from './views/Public/ServiceAgreement/ServiceAgreement';
import { TermsOfUse } from './views/Public/TermsOfUse/TermsOfUse';
import { PrivacyPolicy } from './views/Public/PrivacyPolicy/PrivacyPolicy';
import { Disclaimer } from './views/Public/Disclaimer/Disclaimer';
import { AboutUs } from './views/Public/AboutUs/AboutUs';
import { OurTeam } from './views/Public/OurTeam/OurTeam';
import { OurExpertise } from './views/Public/OurExpertise/OurExpertise';
import { Faq } from './views/Public/Faq/Faq';
import { ContactUs } from './views/Public/ContactUs/ContactUs';
import { AccountDetails } from './views/Investor/AccountDetails/AccountDetails';
import { PhysicalSubmission } from './views/Investor/PhysicalSubmission/PhysicalSubmission';
import { InPersonVerification } from './views/Investor/InPersonVerification/InPersonVerification';
import { Appointments } from './views/Investor/Appointments/Appointments';
import { DocumentVerification } from './views/Admin/DocumentVerification/DocumentVerification';
import { AppointmentManagement } from './views/Admin/AppointmentManagement/AppointmentManagement';
import { AdminDsrDashboard } from './views/Admin/DsrConsole/AdminDsrDashboard';
import { AdminDsrList } from './views/Admin/DsrConsole/AdminDsrList';
import { AdminIdentityReviewList } from './views/Admin/IdentityReview/AdminIdentityReviewList';
import { AdminAuditConsole } from './views/Admin/AuditConsole/AdminAuditConsole';
import { AdminDsrCaseDetail } from './views/Admin/DsrConsole/AdminDsrCaseDetail';
import { AdminDsrUserManagement } from './views/Admin/DsrConsole/AdminDsrUserManagement';
import { FormTemplateList } from './views/Admin/FormTemplates/FormTemplateList';
import { FormTemplateMapping } from './views/Admin/FormTemplates/FormTemplateMapping';
import { BookletComposer } from './views/Admin/FormTemplates/BookletComposer';
import { NextholderManagement } from './views/Investor/NextholderManagement/NextholderManagement';
import { ServiceAgentDashboard } from './views/ServiceAgent/ServiceAgentDashboard/ServiceAgentDashboard';
import { SAJourneySelect } from './views/ServiceAgent/SAJourneySelect/SAJourneySelect';
import { SAInvestorList } from './views/ServiceAgent/InvestorList/InvestorList';
import { ServiceAgentProfile } from './views/ServiceAgent/Profile/ServiceAgentProfile';
import { IntroduceInvestor } from './views/ServiceAgent/IntroduceInvestor/IntroduceInvestor';
import { IntroducedInvestors } from './views/ServiceAgent/IntroducedInvestors/IntroducedInvestors';
import { IntroducedInvestorDetail } from './views/ServiceAgent/IntroducedInvestorDetail/IntroducedInvestorDetail';
import { DelegationManagement } from './views/Investor/DelegationManagement/DelegationManagement';
import { ServiceAgentActivity } from './views/Investor/ServiceAgentActivity/ServiceAgentActivity';
import ServiceAgentProxyWrapper from './components/ServiceAgentProxyWrapper/ServiceAgentProxyWrapper';
import { SAInvestorRedirect } from './views/ServiceAgent/InvestorRedirect';
import SpLanding from './views/ServiceProvider/SpLanding';
import SpUserConsent from './views/ServiceProvider/SpUserConsent';
import SpUserRegister from './views/ServiceProvider/SpUserRegister';
import SpThankYou from './views/ServiceProvider/SpThankYou';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';

import 'bootstrap/dist/css/bootstrap.min.css';
import { ToastContainer } from './components/ToastContainer';
import MainLayout from './components/MainLayout/MainLayout';
import './App.scss';

// When Azure B2C redirects to http://localhost:3000?code=xxx we must send to /login/callback
function RootRedirect() {
  const location = useLocation();
  const fullUrl = location.pathname + location.search + location.hash;
  const hasCode = location.search && location.search.includes('code=');
  const hasHashCode = location.hash && location.hash.includes('code=');
  
  console.log('[RootRedirect] FULL URL:', fullUrl);
  console.log('[RootRedirect] hasCode in search:', hasCode, 'hasCode in hash:', hasHashCode);
  
  if (hasCode) {
    console.log('[RootRedirect] Code detected in query, redirecting to /login/callback');
    return <Navigate to={`/login/callback${location.search}`} replace />;
  }
  
  if (hasHashCode) {
    console.log('[RootRedirect] Code detected in hash fragment, redirecting to /login/callback with hash');
    return <Navigate to={`/login/callback${location.hash}`} replace />;
  }
  
  console.log('[RootRedirect] No code, redirecting to /login');
  return <Navigate to="/login" replace />;
}


const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Public Routes - root with ?code= goes to callback so we don't lose auth code */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login/callback" element={<LoginCallback />} />
          {/* No /forgot-password or /reset-password route: sign-in and password reset are
              Azure AD B2C only (2026-09-12), through its hosted forgotpassword.html and
              resetpassword.html pages. The local pages and the API handlers behind them
              were removed; both paths now fall through to /login. */}
          {/* Self Registration Flow (Multi-step with Laravel theme) */}
          <Route path="/investor/register" element={<MarketInterestCheck />} />
          <Route path="/investor/register/thank-you" element={<ThankYouMessage />} />
          <Route path="/investor/register/email" element={<SelfRegistrationStep1 />} />
          <Route path="/investor/register/consent" element={<SelfRegistrationConsent />} />
          <Route path="/investor/register/otp" element={<SelfRegistrationStep2 />} />
          <Route path="/investor/register/step3" element={<SelfRegistrationStep3 />} />
          <Route path="/investor/register/success" element={<SelfRegistrationSuccess />} />
          {/* FISP-style first-login: email link lands here; component redirects to B2C reset/signin policy.
              The reset policy issues sign-in tokens, so its redirect goes through the standard
              /login/callback → backend JWT exchange → dashboard. No separate /done landing page. */}
          <Route path="/investor/setpassword/:azureUserId" element={<InvestorSetPassword />} />
          {/* Legacy single-page registration (keep for backward compatibility) */}
          
          <Route path="/investor/introduced/register" element={<IntroducedInvestorWizard />} />
          {/* New Introduced Investor Registration Flow (Laravel Parity) */}
          {/* Laravel: introduce-investor1/{Crypt::encrypt(introduce_id)} — same token supported here */}
          <Route path="/introduce-investor1/*" element={<IntroduceInvestor1Redirect />} />
          {/* Alias: Laravel emailed links use the full /account/investor/ prefix */}
          <Route path="/account/investor/introduce-investor1/*" element={<IntroduceInvestor1Redirect />} />
          {/* Single route: * captures plain ss_name or Laravel token (including slashes in base64) */}
          <Route path="/investor/introduced/start/*" element={<IntroducedInvestorStart />} />
          <Route path="/investor/introduced/consent/:investorId" element={<IntroducedInvestorConsent />} />
          <Route path="/investor/introduced/step1/:uniqueCode" element={<IntroducedInvestorStep1 />} />
          <Route path="/investor/introduced/step2/:uniqueCode" element={<IntroducedInvestorStep2 />} />
          <Route path="/investor/introduced/step4/:uniqueCode" element={<IntroducedInvestorStep4 />} />
          <Route path="/investor/introduced/success" element={<IntroducedInvestorSuccess />} />
          <Route path="/investor/pms/register" element={<PmsInvestorWizard />} />
          <Route path="/investor/document-submission/:token" element={<PublicDocumentSubmission />} />
          {/* SP re-edit decision page — token-authenticated, no login (links from email template 38) */}
          <Route path="/re-edit/:token" element={<ReEditAction />} />
          <Route path="/re-edit/:token/:action" element={<ReEditAction />} />
          <Route
            path="/investor/profile-pdf"
            element={
              <ProtectedRoute>
                <InvestorProfilePdf />
              </ProtectedRoute>
            }
          />
          {/* Service Provider Onboarding (Laravel parity — entry email links to /account/investor/step?status=) */}
          <Route path="/account/investor/step" element={<SpLanding />} />
          <Route path="/service-provider/user-consent" element={<SpUserConsent />} />
          <Route path="/service-provider/user-register" element={<SpUserRegister />} />
          <Route path="/service-provider/thank-you" element={<SpThankYou />} />

          <Route path="/service-agreement" element={<ServiceAgreement />} />
          {/* Laravel serves Terms of Use at /terms-use — same path here. */}
          <Route path="/terms-use" element={<TermsOfUse />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          {/* Company pages are served by this app, in our own theme, so an investor
              never leaves the platform to read them. */}
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/our-team" element={<OurTeam />} />
          <Route path="/our-expertise" element={<OurExpertise />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/dashboard"
            element={
              <ProtectedRoute>
                <MainLayout title="Investor Console" subtitle="Overview of your profile, consents, journey, and services">
                  <InvestorDashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/progress"
            element={
              <ProtectedRoute>
                <MainLayout title="My Journey" subtitle="Track your onboarding and application journey">
                  <InvestorProgress />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {/* Journeys list — one row per assigned product; click-through to a specific journey */}
          <Route
            path="/investor/journeys"
            element={
              <ProtectedRoute>
                <MainLayout title="My Onboarding Status" subtitle="Your product journeys — select one to continue">
                  <JourneyList />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/journey"
            element={
              <ProtectedRoute>
                <MainLayout title="My Journey" subtitle="Track and complete your onboarding journey">
                  <InformationContainer />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {/* Specific journey by id */}
          <Route
            path="/investor/journey/:journeyId"
            element={
              <ProtectedRoute>
                <MainLayout title="My Journey" subtitle="Track and complete your onboarding journey">
                  <InformationContainer />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {/* My Profile — read-only personal profile summary (Laravel investor-details parity) */}
          <Route
            path="/investor/profile"
            element={
              <ProtectedRoute>
                <MainLayout title="My Profile" subtitle="Your personal profile details">
                  <MyProfile />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {/* My Consents — Consent Centre detail (Privacy, Platform Terms, SOW, Marketing, WhatsApp) */}
          <Route
            path="/investor/consents"
            element={
              <ProtectedRoute>
                <MainLayout title="Consent Centre" subtitle="Manage your consents and Statement of Work">
                  <MyConsents />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {/* Documents Center — Smart Upload (OCR) KYC document page */}
          <Route
            path="/investor/documents-center"
            element={
              <ProtectedRoute>
                <MainLayout title="Documents Center" subtitle="Upload your KYC documents — we extract and validate key fields automatically via OCR">
                  <SmartUploadTab />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/dsr-center"
            element={
              <ProtectedRoute>
                <MainLayout title="DSR Center" subtitle="Manage your data requests and privacy preferences.">
                  <DsrCenter />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/dsr-center/requests"
            element={
              <ProtectedRoute>
                <MainLayout title="My DSR Requests" subtitle="Track your data privacy and rights requests here">
                  <DsrRequests />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/dsr-center/:caseId"
            element={
              <ProtectedRoute>
                <MainLayout title="DSR Case Detail" subtitle="View details of your DSR case">
                  <DsrCaseDetail />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/documents"
            element={
              <ProtectedRoute>
                <MainLayout title="KYC Documents" subtitle="Upload and manage your KYC documents">
                  <DocumentUpload />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/onboarding"
            element={
              <ProtectedRoute>
                <MainLayout title="Onboarding Documents" subtitle="Upload and manage your onboarding documents">
                  <OnboardingDocuments />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/account-details"
            element={
              <ProtectedRoute>
                <MainLayout title="Account Details" subtitle="View your bank and trading account details">
                  <AccountDetails />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/physical-submission"
            element={
              <ProtectedRoute>
                <MainLayout title="Physical Submission" subtitle="Submit physical documents via courier or in person">
                  <PhysicalSubmission />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/verification"
            element={
              <ProtectedRoute>
                <MainLayout title="" subtitle="">
                  <InPersonVerification />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/appointments"
            element={
              <ProtectedRoute>
                <Appointments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/profile"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/nextholders"
            element={
              <ProtectedRoute>
                <NextholderManagement />
              </ProtectedRoute>
            }
          />

          {/* Investor delegation management */}
          <Route
            path="/investor/delegations"
            element={
              <ProtectedRoute>
                <MainLayout title="Permissions Center" subtitle="Manage which Service Agents can access your account and what they're allowed to do.">
                  <DelegationManagement />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {/* Grant delegation route removed - only SP can assign Service Agents */}
          {/* <Route
            path="/investor/delegations/grant"
            element={
              <ProtectedRoute>
                <GrantDelegation />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/investor/service-agent-activity"
            element={
              <ProtectedRoute>
                <MainLayout title="Agent Activity" subtitle="All actions performed by Service Agents on your account.">
                  <ServiceAgentActivity />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Service Agent Routes */}
          <Route
            path="/service-agent/dashboard"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <MainLayout title="Client Executive Agent Console" subtitle="Manage investors assigned to you via active delegations">
                  <ServiceAgentDashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-agent/investors"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <MainLayout title="Client Executive Agent Console" subtitle="Manage investors assigned to you via active delegations">
                  <SAInvestorList />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Introduce Investor — a CEA creates an introduction for their own service provider */}
          <Route
            path="/service-agent/introduce-investor"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <MainLayout title="Client Executive Agent Console" subtitle="Introduce an investor on behalf of your service provider">
                  <IntroduceInvestor />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-agent/introduced-investors"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <MainLayout title="Client Executive Agent Console" subtitle="Investors you have introduced">
                  <IntroducedInvestors />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-agent/introduced-investors/:id"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <MainLayout title="Client Executive Agent Console" subtitle="Introduction details">
                  <IntroducedInvestorDetail />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          {/* SA Investor Proxy Routes - reuse investor components with delegation banner */}
          <Route
            path="/service-agent/investors/:investorId/journeys"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <ServiceAgentProxyWrapper>
                  <SAJourneySelect />
                </ServiceAgentProxyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-agent/investors/:investorId/profile"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <ServiceAgentProxyWrapper>
                  <InformationContainer />
                </ServiceAgentProxyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-agent/investors/:investorId/documents"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <ServiceAgentProxyWrapper>
                  <DocumentUpload />
                </ServiceAgentProxyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-agent/investors/:investorId/onboarding"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <ServiceAgentProxyWrapper>
                  {/* Was InformationContainer (2026-09-06): the progress bar's "Onboarding"
                      step opened the Information tabs again instead of the signed-forms
                      page the investor route mounts. */}
                  <OnboardingDocuments />
                </ServiceAgentProxyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-agent/investors/:investorId/physical-submission"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <ServiceAgentProxyWrapper>
                  <PhysicalSubmission />
                </ServiceAgentProxyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-agent/investors/:investorId/account-details"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <ServiceAgentProxyWrapper>
                  <AccountDetails />
                </ServiceAgentProxyWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-agent/investors/:investorId/verification"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <ServiceAgentProxyWrapper>
                  <InPersonVerification />
                </ServiceAgentProxyWrapper>
              </ProtectedRoute>
            }
          />
          {/* Preview & Submit opens in a NEW TAB — this SA-scoped route lets the wrapper
              re-establish proxy mode from the URL there (the adapter's in-memory proxy id
              does not survive a fresh tab; opening /investor/profile-pdf bounced the agent
              to B2C, seen live 2026-09-05). */}
          <Route
            path="/service-agent/investors/:investorId/profile-pdf"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <ServiceAgentProxyWrapper>
                  <InvestorProfilePdf />
                </ServiceAgentProxyWrapper>
              </ProtectedRoute>
            }
          />
          
          {/* Redirect old URL to new page-based flow */}
          <Route
            path="/service-agent/investors/:investorId"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <SAInvestorRedirect />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/service-agent/profile"
            element={
              <ProtectedRoute requiredRoles={['SERVICE_AGENT']}>
                <MainLayout title="Client Executive Agent Console" subtitle="Manage investors assigned to you via active delegations">
                  <ServiceAgentProfile />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <MainLayout title="">
                  <AdminDsrUserManagement />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dsr"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN', 'DSR_ADMIN']}>
                <MainLayout title="">
                  <AdminDsrList />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dsr/:caseId"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN', 'DSR_ADMIN']}>
                <MainLayout title="">
                  <AdminDsrCaseDetail />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dsr-admins"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN', 'DSR_ADMIN']}>
                <MainLayout title="">
                  <AdminDsrDashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/identity-reviews"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <MainLayout title="">
                  <AdminIdentityReviewList />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <MainLayout title="">
                  <AdminAuditConsole />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/batch-register"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <BatchRegistrationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <AdminInvestorList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/:clientId"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <AdminInvestorDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/investor-type-categories"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <InvestorTypeCategoryManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/market-types"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <MarketTypeManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/nationalities"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <NationalityManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/document-verification"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <DocumentVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/appointments"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <AppointmentManagement />
              </ProtectedRoute>
            }
          />
          {/* SP Form Templates (SP_DOCUMENT_ONBOARDING_PLAN.md) — isolated admin feature */}
          <Route
            path="/admin/form-templates"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <MainLayout title="">
                  <FormTemplateList />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/form-templates/compose"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <MainLayout title="">
                  <BookletComposer />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/form-templates/:id"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN']}>
                <MainLayout title="">
                  <FormTemplateMapping />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/broker/dashboard"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN', 'BROKER']}>
                <BrokerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/broker/my-agents"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'PLATFORM_SUPER_ADMIN', 'BROKER']}>
                <MyAgents />
              </ProtectedRoute>
            }
          />

          {/* Super Admin Routes */}
          <Route
            path="/super-admin/dashboard"
            element={
              <ProtectedRoute requiredRoles={['PLATFORM_SUPER_ADMIN']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/tenants"
            element={
              <ProtectedRoute requiredRoles={['PLATFORM_SUPER_ADMIN']}>
                <TenantManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/users"
            element={
              <ProtectedRoute requiredRoles={['PLATFORM_SUPER_ADMIN']}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/roles"
            element={
              <ProtectedRoute requiredRoles={['PLATFORM_SUPER_ADMIN']}>
                <RoleManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/user-groups"
            element={
              <ProtectedRoute requiredRoles={['PLATFORM_SUPER_ADMIN']}>
                <UserGroupManagement />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ToastContainer />
      </Router>
    </AuthProvider>
  );
};

export default App;
